// Typed client for the /projects endpoints (the Projects agent workspace).
// JSON calls go through the shared `api` client; the multipart upload uses raw
// fetch + manual Bearer the same way files.ts does.
import { api, API_BASE_URL, API_PREFIX, ApiError } from "./client";
import { tokenStore } from "./token-store";
import type { ReportBib, ReportBibCandidate } from "./report";

export type ProjectStatus = "draft" | "ready" | "running" | "complete" | "failed";
export type FileRole = "info" | "task" | "ignore";
export type ImagePolicy = "all" | "none" | "marked";
// Whether a material file is a primary source ("main") or supporting context
// ("support"). Only applies to material (role "info"), not tasks/ignored files.
export type SourceTier = "main" | "support";
// single = one task file → one worked solution. multi_sheet = a coursework folder
// of many task sheets, each solved individually and rolled into one output.
// material = "understand this material" — no task file at all (papers, notes);
// the pipeline skips task-solving and the recap covers the breadth.
export type ProjectMode = "single" | "multi_sheet" | "material";

export const PROJECT_IN_FLIGHT: ProjectStatus[] = ["running"];

export interface ProjectDeepMark {
  id: string;
  label?: string;
  depth?: string;
  page?: number;
  rect?: { x: number; y: number; w: number; h: number };
  selected_text?: string;
  image_key?: string;
  image_url?: string;
  image_b64?: string; // only sent up for new snips; never echoed back
  directives?: string[];
  // "Make a label-the-diagram question from this image" + free-text precision.
  // Forces an `image_labeling` question on this figure (additive, candidacy-bypassed).
  label_question?: boolean;
  label_instruction?: string;
  // Lookup fields (the `lookups` array reuses this shape). `kind`/`explanation`
  // plus the specialist deep-card facets, so an "Explain in depth" mark survives
  // round-trip + flows into generation.
  kind?: string;
  explanation?: string;
  definition?: string;
  intuition?: string;
  question?: string;
  answer?: string;
}
export interface ProjectFileMarks {
  deep: ProjectDeepMark[];
  lookups: ProjectDeepMark[];
}

export interface ProjectFileRead {
  id: string;
  filename: string;
  relative_path: string;
  file_type: string;
  role: FileRole;
  image_policy: ImagePolicy;
  source_tier?: SourceTier;
  extracted_chars: number | null;
  order: number;
  created_at: string;
  download_url: string | null;
  marks: ProjectFileMarks | null;
  has_marks: boolean;
}

/** Decoupled question-set generation lifecycle (independent of `status`, which
 *  reaches "complete" when the SUMMARY finishes). */
export type QuestionSetStatus = "idle" | "running" | "complete" | "failed";

export interface ProjectSummary {
  id: string;
  title: string;
  status: ProjectStatus;
  mode: ProjectMode;
  file_count: number;
  produced_question_set_id: string | null;
  question_set_status: QuestionSetStatus;
  created_at: string;
  updated_at: string;
  archived_at: string | null;
}

export interface ProjectPlot {
  url: string | null;
  caption: string | null;
}
export interface ProjectAiImage {
  url: string | null;
  caption: string | null;
  prompt?: string | null;
}

export interface ProjectFigure {
  url: string | null;
  caption: string | null;
  topic_slug: string | null;
  source_page: number | null;
  cleaned: boolean;
}
export interface ProjectCodeBlock {
  language: string;
  code: string;
  label?: string | null;
}

export type TaskKind = "code" | "derivation" | "written";

export interface ParamSpecItem {
  name: string;
  label: string;
  min: number;
  max: number;
  step: number;
  default: number;
  unit?: string | null;
}

export interface DerivationStep {
  order: number;
  latex: string;
  explanation: string;
}

export interface SolutionVerification {
  checked: boolean;
  passed: boolean;
  detail: string;
}

export interface ProjectCostStep {
  part_slug: string;
  model: string | null;
  prompt_tokens: number | null;
  completion_tokens: number | null;
  est_cost_usd: number | null;
}

export interface ProjectCost {
  raw_total_usd: number;
  markup: number;
  charged_total_usd: number;
  currency: string;
  prompt_tokens: number | null;
  completion_tokens: number | null;
  image_count: number;
  image_cost_usd: number;
  model_mix: Record<string, number> | null;
  steps: ProjectCostStep[];
}

// Per-topic analysis the project agent now emits (mirrors the produced question
// set's arc). `summary_card` is the topic's cheat/summary card (same shape as a
// CheatSheetItem); `figure_urls` are this topic's real figures (matched against
// `figures[].topic_slug` for captions + source page).
export interface KeyFormula {
  label: string;
  expression: string;
  note?: string | null;
}
export interface KeyMethod {
  name: string;
  steps: string[];
  when_to_use?: string | null;
}
export interface SummaryCard {
  summary: string;
  key_formulas: KeyFormula[];
  key_methods: KeyMethod[];
  common_pitfalls: string[];
}
/** One turn in a section's "Ask the expert" thread (server-persisted). */
export interface SectionQATurn {
  id: string;
  question: string;
  answer: string;
  model?: string;
  asked_at?: string;
}
/** One expert-generated image folded under a topic section. */
export interface SectionFigure {
  id: string;
  url?: string | null;
  caption?: string;
  prompt?: string;
  created_at?: string;
}
export interface TopicSection {
  slug: string;
  label: string;
  summary: string;
  summary_card: SummaryCard | null;
  figure_urls: string[];
  /** Persisted per-section Q&A thread, folded in by the API on read. */
  expert_thread?: SectionQATurn[];
  /** Expert-generated images for this topic, folded in by the API on read. */
  generated_figures?: SectionFigure[];
  /** Course → unit → topic grouping (Phase 4). Empty for short material → flat. */
  unit?: string;
  unit_label?: string;
}

/** A unit/chapter in the course structure (Phase 4). */
export interface CourseUnit {
  slug: string;
  label: string;
  summary?: string;
}

/** The denormalized agent result, with object-storage URLs minted by the API. */
export interface ProjectResults {
  task_kind: TaskKind;
  recap_markdown: string;
  key_points: string[];
  solution_markdown: string;
  code_blocks: ProjectCodeBlock[];
  stdout: string;
  solved: boolean;
  attempts: number;
  plot_image_urls: ProjectPlot[];
  supplementary_image_urls: ProjectPlot[];
  // Faithful plots of the functions the material is about (project.function-plots).
  function_plot_image_urls?: ProjectPlot[];
  ai_image_urls: ProjectAiImage[];
  aux_image_urls: ProjectPlot[];
  figures: ProjectFigure[];
  solution_pdf_url: string | null;
  solution_py_url: string | null;
  task_filename: string | null;
  // derivation / written
  derivation_steps: DerivationStep[];
  final_answer: string;
  verification: SolutionVerification | null;
  // interactive explorer (parameterized_code stays server-side)
  parameter_spec: ParamSpecItem[];
  // billing-grade cost (raw + charged + per-step)
  cost: ProjectCost | null;
  // adaptive topic-specific analysis (optional — absent on older results)
  domain?: string | null;
  subject_tags?: string[];
  // Field-expert that handled the solve (Phase 5 badge). origin ∈
  // specialist | curated | generated | generic.
  field_label?: string | null;
  specialization_origin?: string | null;
  topic_sections?: TopicSection[];
  // Course structure (Phase 4) — units that topic_sections group under. Empty/
  // absent for short material → topics render as a flat list.
  units?: CourseUnit[];
  // multi_sheet projects only: one worked solution per task sheet. Absent for
  // single-sheet projects (which use the flat solution_* fields above).
  task_solutions?: TaskSolution[];
  // the background understanding behind the solution (markdown)
  background?: string;
  // tappable references the solution relied on → open the file at the passage
  source_references?: SourceReference[];
}

// One solved task sheet in a multi_sheet project. Mirrors the per-sheet fields of
// TaskSolveOutput; plot keys are minted into `plot_image_urls` by the API.
export interface TaskSolution {
  file_id: string;
  task_filename: string;
  task_kind: TaskKind;
  narrative: string;
  code_blocks: ProjectCodeBlock[];
  stdout: string;
  derivation_steps: DerivationStep[];
  final_answer: string;
  background?: string;
  plot_image_urls: ProjectPlot[];
  supplementary_image_urls: ProjectPlot[];
}

export interface SourceReference {
  file_id: string;
  filename: string;
  quote: string;
  context: string;
  char_offset?: number;
  page?: number;
  cite_key?: string;
}

export interface ExploreResponse {
  ok: boolean;
  plot_image_urls?: ProjectPlot[];
  stderr?: string;
  params: Record<string, number>;
}

export interface ProjectRead extends ProjectSummary {
  description: string | null;
  // Persistent kickoff brief — the default for summary/question-set/report/
  // presentation kickoffs (explicit per-request briefs win).
  brief: string | null;
  source_run_id: string | null;
  files: ProjectFileRead[];
  result: ProjectResults | null;
  // Marks the user made on the response text (recap/background/solution/
  // summaries). null = unmarked. Same shape as per-file marks.
  result_marks: ProjectFileMarks | null;
}

export interface ClassifySuggestion {
  file_id: string;
  suggested_role: FileRole;
  rationale: string;
}

// A run's poll shape — a subset of the shared RunRead.
export interface ProjectRunStep {
  index: number;
  part_slug: string;
  status: string;
  error_message: string | null;
  updated_at: string;
}
export interface ProjectRunRead {
  id: string;
  status: string;
  current_step_index: number;
  produced_question_set_id: string | null;
  error_message: string | null;
  steps: ProjectRunStep[];
}

const enc = encodeURIComponent;

export interface MarkedFile {
  file_id: string;
  filename: string;
  file_type: string;
  project_id: string;
  project_title: string;
  deep_count: number;
  lookup_count: number;
}

/** Files the user has MARKED (in any project), loadable into a framework run via
 *  start_inputs.source_file_ids — the "load an already-marked file" path. */
export const listMarkedFiles = () => api.get<MarkedFile[]>("/projects/marked-files");

export const listProjects = (includeArchived = false) =>
  api.get<ProjectSummary[]>("/projects/", { query: { include_archived: includeArchived } });
export const getProject = (id: string) => api.get<ProjectRead>(`/projects/${enc(id)}`);
export const createProject = (
  title: string,
  description?: string,
  mode: ProjectMode = "single",
  brief?: string,
) =>
  api.post<ProjectRead>("/projects/", {
    title,
    description: description ?? null,
    mode,
    ...(brief?.trim() ? { brief: brief.trim() } : {}),
  });
export const deleteProject = (id: string) => api.delete<void>(`/projects/${enc(id)}`);

/** Edit project-level kickoff metadata. `brief: ""` clears the brief. */
export const patchProject = (
  id: string,
  body: { title?: string; description?: string | null; brief?: string },
) => api.patch<ProjectRead>(`/projects/${enc(id)}`, body);

export const setProjectMode = (id: string, mode: ProjectMode) =>
  api.patch<ProjectSummary>(`/projects/${enc(id)}/mode`, { mode });

export const patchProjectFile = (
  id: string,
  fileId: string,
  body: { role?: FileRole; order?: number; image_policy?: ImagePolicy; source_tier?: SourceTier },
) => api.patch<ProjectFileRead>(`/projects/${enc(id)}/files/${enc(fileId)}`, body);

export const deleteProjectFile = (id: string, fileId: string) =>
  api.delete<void>(`/projects/${enc(id)}/files/${enc(fileId)}`);

export const classifyProjectFiles = (id: string) =>
  api.post<{ suggestions: ClassifySuggestion[] }>(`/projects/${enc(id)}/classify`, {});

// ── project-level bibliography (canonical; the report inherits it) ───────────
export const getProjectBib = (id: string) =>
  api.get<ReportBib>(`/projects/${enc(id)}/bib`);
export const extractProjectBib = (id: string) =>
  api.post<ReportBib>(`/projects/${enc(id)}/bib/extract`, {});
export const putProjectBib = (id: string, bibSource: string) =>
  api.put<ReportBib>(`/projects/${enc(id)}/bib`, { bib_source: bibSource });
export const searchProjectBib = (
  id: string,
  body: { query: string; source?: "all" | "crossref" | "arxiv"; limit?: number },
) => api.post<{ candidates: ReportBibCandidate[] }>(`/projects/${enc(id)}/bib/search`, body);

export const runProject = (
  id: string,
  opts?: {
    language?: string;
    max_files?: number;
    max_solve_attempts?: number;
    max_figures?: number;
    sandbox_timeout_s?: number;
    // Weave the synthesis engine: also generate diverse perspective-shift items.
    with_synthesis?: boolean;
    synthesis_strategy?: "broad" | "deep";
    // Optional free-text brief steering the run (angle/emphasis). Omit when empty.
    brief?: string;
  },
) => api.post<ProjectRead>(`/projects/${enc(id)}/run`, opts ?? {});

/** Poll a project until its task file's text extraction finishes (draft→ready),
 *  or `timeoutMs` elapses. Extraction is async on the server, so a run started
 *  immediately after upload would 409 ("still reading your file"). Returns
 *  whether it became runnable. */
export async function waitForProjectReady(id: string, timeoutMs = 120_000): Promise<boolean> {
  const deadline = Date.now() + timeoutMs;
  while (Date.now() < deadline) {
    try {
      const p = await getProject(id);
      if (p.status === "ready" || p.status === "running" || p.status === "complete") return true;
      if (p.status === "failed") return false;
    } catch {
      /* transient — keep polling */
    }
    await new Promise((r) => setTimeout(r, 2000));
  }
  return false;
}

/** Generate a trainable question set FROM a finished project summary (async).
 *  Reuses the summary run's outputs + folds in result_marks/result_qa server-side.
 *  Poll with getQuestionSetRun. */
export const generateQuestionSet = (
  id: string,
  opts?: {
    language?: string;
    max_files?: number;
    max_solve_attempts?: number;
    max_figures?: number;
    sandbox_timeout_s?: number;
    with_synthesis?: boolean;
    synthesis_strategy?: "broad" | "deep";
    // Optional free-text brief steering the run (angle/emphasis). Omit when empty.
    brief?: string;
  },
) => api.post<ProjectRead>(`/projects/${enc(id)}/generate-question-set`, opts ?? {});

/** Poll the decoupled question-set run (same RunRead shape as getProjectRun). */
export const getQuestionSetRun = (id: string) =>
  api.get<ProjectRunRead>(`/projects/${enc(id)}/generate-question-set`);

export const archiveProject = (id: string, archived: boolean) =>
  api.patch<ProjectSummary>(`/projects/${enc(id)}/archive`, { archived });

export const saveFileMarks = (
  projectId: string,
  fileId: string,
  marks: ProjectFileMarks,
) =>
  api.patch<ProjectFileRead>(
    `/projects/${enc(projectId)}/files/${enc(fileId)}/marks`,
    { marks },
  );

/** Autosave the user's marks on the project's RESPONSE TEXT (text-only). */
export const saveResultMarks = (projectId: string, marks: ProjectFileMarks) =>
  api.patch<ProjectRead>(`/projects/${enc(projectId)}/result-marks`, { marks });

export const getProjectRun = (id: string) =>
  api.get<ProjectRunRead>(`/projects/${enc(id)}/run`);

export const exploreProject = (id: string, params: Record<string, number>) =>
  api.post<ExploreResponse>(`/projects/${enc(id)}/explore`, { params });

export interface SectionExpertAskResponse {
  topic_slug: string;
  turn: SectionQATurn;
  thread: SectionQATurn[];
  model?: string;
}
/** Ask a free-form question of one summary section's specialised expert. */
export const askSectionExpert = (
  projectId: string,
  topicSlug: string,
  body: { question: string; language?: string },
) =>
  api.post<SectionExpertAskResponse>(
    `/projects/${enc(projectId)}/sections/${enc(topicSlug)}/ask`,
    body,
  );

export interface SectionVisualizeResponse {
  topic_slug: string;
  figure: SectionFigure;
  figures: SectionFigure[];
  model?: string;
}
/** Generate an image for one summary section from a free-form description. */
export const generateSectionFigure = (
  projectId: string,
  topicSlug: string,
  body: { demand: string; language?: string },
) =>
  api.post<SectionVisualizeResponse>(
    `/projects/${enc(projectId)}/sections/${enc(topicSlug)}/visualize`,
    body,
  );

/** Multipart single-file upload. `role` defaults to info; mark the task later. */
export async function uploadProjectFile(
  id: string,
  file: File,
  relativePath: string,
  role: FileRole = "info",
  imagePolicy?: ImagePolicy,
  sourceTier?: SourceTier,
): Promise<ProjectFileRead> {
  const form = new FormData();
  form.append("file", file, file.name);
  form.append("relative_path", relativePath);
  form.append("role", role);
  if (imagePolicy) form.append("image_policy", imagePolicy);
  if (sourceTier) form.append("source_tier", sourceTier);

  const headers: Record<string, string> = { Accept: "application/json" };
  const token = tokenStore.access;
  if (token) headers.Authorization = `Bearer ${token}`;
  // Do NOT set Content-Type — the browser sets the multipart boundary.

  const res = await fetch(`${API_BASE_URL}${API_PREFIX}/projects/${enc(id)}/files`, {
    method: "POST",
    headers,
    body: form,
  });
  if (!res.ok) {
    let message = `Upload failed (${res.status})`;
    try {
      const data = await res.json();
      if (typeof data?.detail === "string") message = data.detail;
    } catch {
      /* keep generic */
    }
    throw new ApiError(res.status, message);
  }
  return (await res.json()) as ProjectFileRead;
}
